import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Stepper } from '../ui/Stepper';
import { Upload as UploadIcon, FileText, CheckCircle, WarningCircle, X } from '@phosphor-icons/react';
import { fileApi } from '../../services/endpoints';
import { saveNavigationState } from '../../utils/navigationStateManager';
import type { UploadedFile } from '../../services/types';

export function UploadPrivate() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'success' | 'error'>('idle');
  const [validationMessage, setValidationMessage] = useState('');
  const [previewData, setPreviewData] = useState<string[][] | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
  const [uploadedFileData, setUploadedFileData] = useState<UploadedFile | null>(null);

  const steps = [
    { label: 'Upload', status: 'current' as const },
    { label: 'Validate', status: validationStatus === 'success' ? 'completed' as const : 'upcoming' as const },
    { label: 'Select Signature', status: 'upcoming' as const },
    { label: 'Run', status: 'upcoming' as const }
  ];

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = async (uploadedFile: File) => {
    setFile(uploadedFile);
    setValidationStatus('validating');
    setUploadProgress(0);
    setPreviewData(null);
    
    try {
      // Upload file with progress tracking
      const response = await fileApi.uploadFile(uploadedFile, (progress) => {
        setUploadProgress(progress);
      });
      
      const { data } = response;
      setUploadedFileId(data.file_id);
      
      // Fetch full file details including preview
      const fileResponse = await fileApi.getFile(data.file_id);
      const fileData: UploadedFile = fileResponse.data;
      setUploadedFileData(fileData);
      
      // Check validation status
      if (fileData.validation_status === 'valid') {
        setValidationStatus('success');
        setValidationMessage(
          `File validated successfully. Gene expression matrix detected with ${fileData.gene_count?.toLocaleString() || 'N/A'} genes and ${fileData.sample_count || 'N/A'} samples.`
        );
        
        // Format preview data if available
        if (fileData.preview_data) {
          const head = fileData.preview_data.head;
          const previewTable: string[][] = Array.isArray(head)
            ? head.filter((row): row is string[] => Array.isArray(row))
            : [];

          setPreviewData(previewTable.length > 0 ? previewTable : null);
        }
      } else if (fileData.validation_status === 'invalid') {
        setValidationStatus('error');
        setValidationMessage(fileData.validation_message || 'File validation failed. Please check the file format.');
      } else {
        // Status is 'pending', wait for validation
        setValidationStatus('validating');
        setValidationMessage('Processing file, please wait...');
      }
      
    } catch (error: unknown) {
      console.error('Upload error:', error);
      setValidationStatus('error');

      // Extract error message
      let errorMsg = 'Failed to upload file. ';
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
      if (typeof detail === 'string') {
        errorMsg += detail;
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        errorMsg += String((detail as { message: unknown }).message);
      } else if (error instanceof Error) {
        errorMsg += error.message;
      } else {
        errorMsg += 'Please try again.';
      }

      setValidationMessage(errorMsg);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setValidationStatus('idle');
    setValidationMessage('');
    setPreviewData(null);
    setUploadProgress(0);
    setUploadedFileId(null);
    setUploadedFileData(null);
  };

  const handleContinue = () => {
    if (uploadedFileId) {
      const navigationState = { 
        dataType: 'private',
        fileId: uploadedFileId,
        fileData: uploadedFileData
      };
      saveNavigationState('select-signature', navigationState);
      navigate('/select-signature', { 
        state: navigationState
      });
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Stepper steps={steps} />

        <div className="mb-8">
          <button
            onClick={() => navigate('/upload')}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-2 transition-colors text-sm"
          >
            ← Back to upload options
          </button>
          <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-3">
            Private upload
          </span>
          <h1 className="text-foreground mb-2">Upload your data.</h1>
          <p className="text-muted-foreground">
            Upload your gene expression table to begin analysis. We accept standard formats with genes in rows and samples in columns.
          </p>
        </div>

        {/* Upload Card */}
        <Card className="p-8 mb-6">
          <div className="mb-6">
            <h3 className="mb-2">Gene Expression Data</h3>
            <p className="text-muted-foreground">
              Supported formats: CSV, TSV, TXT
            </p>
          </div>

          {!file ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
            >
              <UploadIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-foreground mb-2">
                Drag and drop your file here, or
              </p>
              <label className="inline-block">
                <span className="text-primary cursor-pointer hover:text-primary/80 transition-colors">
                  browse files
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv,.txt,.tsv"
                  onChange={handleFileInput}
                />
              </label>
              <p className="text-muted-foreground mt-4">
                Maximum file size: 100MB
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-foreground">{file.name}</p>
                    <p className="text-muted-foreground">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveFile}
                  className="p-2 hover:bg-muted-foreground/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              {/* Validation Status */}
              {validationStatus === 'validating' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
                    <div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">
                      {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Validating file...'}
                    </p>
                  </div>
                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              {validationStatus === 'success' && (
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-foreground">{validationMessage}</p>
                  </div>
                </div>
              )}

              {validationStatus === 'error' && (
                <div className="flex items-start gap-3 p-4 border border-border rounded-lg">
                  <WarningCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-destructive">{validationMessage}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* File Metadata */}
        {uploadedFileData && validationStatus === 'success' && (
          <Card className="p-6 mb-6">
            <h3 className="mb-4">File Information</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-muted-foreground text-sm">Gene Count</p>
                <p className="text-foreground font-medium">{uploadedFileData.gene_count?.toLocaleString() || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Sample Count</p>
                <p className="text-foreground font-medium">{uploadedFileData.sample_count || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Gene ID Type</p>
                <p className="text-foreground font-medium">{uploadedFileData.gene_id_type?.toUpperCase() || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Expression Type</p>
                <p className="text-foreground font-medium">{uploadedFileData.expression_type?.toUpperCase() || 'Unknown'}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Preview Table */}
        {previewData && (
          <Card className="p-6 mb-6">
            <h3 className="mb-4">Data Preview</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    {previewData[0].map((header, idx) => (
                      <th key={idx} className="px-4 py-3 text-left text-foreground bg-muted">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(1).map((row, rowIdx) => (
                    <tr key={rowIdx} className="border-b border-border/50">
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="px-4 py-3 text-muted-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground mt-4">
              Showing first {previewData.length - 1} rows of {uploadedFileData?.gene_count?.toLocaleString() || 'N/A'} total genes
            </p>
          </Card>
        )}

        {/* File Format Info */}
        <div className="p-6 mb-8 border border-border rounded-lg">
          <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-3">
            Expected file format
          </p>
          <div className="divide-y divide-border">
            {[
              'First column: gene identifiers (ENSEMBL IDs, gene symbols, etc.)',
              'Subsequent columns: sample expression values (numeric)',
              'Header row with sample names recommended',
              'No missing values in expression data',
            ].map(item => (
              <p key={item} className="py-2 text-sm text-muted-foreground">{item}</p>
            ))}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button variant="secondary" onClick={() => navigate('/upload')}>
            Back
          </Button>
          <Button
            variant="default"
            disabled={validationStatus !== 'success'}
            onClick={handleContinue}
          >
            Continue to Signature Selection
          </Button>
        </div>
      </div>
    </div>
  );
}
